/**
 * Provide/inject for the player's loader: where an embed comes from.
 *
 * A player does not know its context — an embed page, a share page, the application — and a
 * parent says which by providing the loader, so the nested embeds (VideoEmbed, AudioEmbed, …) read
 * the right source without prop drilling. Every loader answers the player's own `Embed`, already
 * transformed from whichever shape its source speaks.
 */

import { inject, provide, type App, type InjectionKey } from 'vue'
import type { Embed } from '../../types/embed'
import type { EmbedAudience } from '../embedFailure'

/** What a player may present when it opens a publication. */
export interface EmbedLoaderParams {
  /** An access token, for a token-protected embed. */
  token?: string
  /** A link password, for a password-protected share opened through its embed page. */
  password?: string
  /** The per-device token the player already holds. */
  viewerId?: string
  /**
   * A preset to resolve the look with, first in the cascade: the one an embed page's URL named,
   * or the one the application previews (`docs/plans/embeds-rust.md` ER11).
   */
  preset?: string
}

/** Loading player data by id: an embed's uid, a shared asset's, or an asset's in the application. */
export type EmbedLoaderApiFn = (id: string, params: EmbedLoaderParams) => Promise<Embed>

const EMBED_LOADER_API_KEY: InjectionKey<EmbedLoaderApiFn> = Symbol('embed-loader-api')

const EMBED_AUDIENCE_KEY: InjectionKey<EmbedAudience> = Symbol('embed-audience')

/**
 * Provide a loader to all child components.
 *
 * Call this in a parent component's `setup()` to replace the public embed read `useEmbedLoader`
 * falls back to. `audience` says who reads a failure: a `member` of the organization in the
 * application, for whom a refusal is a permission and not a site the owner did not allow.
 */
export function provideEmbedLoaderApi(apiFn: EmbedLoaderApiFn, audience: EmbedAudience = 'visitor'): void {
  provide(EMBED_LOADER_API_KEY, apiFn)
  provide(EMBED_AUDIENCE_KEY, audience)
}

/**
 * App-level variant of {@link provideEmbedLoaderApi}, for the standalone embed
 * entry (`main.ts`) which mounts `EmbedPlayer` as the root component and has no
 * ancestor `setup()` to call the component-level provide from.
 */
export function provideEmbedLoaderApiToApp(app: App, apiFn: EmbedLoaderApiFn): void {
  app.provide(EMBED_LOADER_API_KEY, apiFn)
}

/**
 * Inject the loader provided by a parent component.
 *
 * Returns `undefined` if no parent has called `provideEmbedLoaderApi()`,
 * in which case `useEmbedLoader` falls back to the public embed read.
 */
export function useInjectedEmbedLoaderApi(): EmbedLoaderApiFn | undefined {
  return inject(EMBED_LOADER_API_KEY, undefined)
}

/** Who reads the player's failures: a visitor unless a parent provided a member's loader. */
export function useInjectedEmbedAudience(): EmbedAudience {
  return inject(EMBED_AUDIENCE_KEY, 'visitor')
}
